import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';

import { modelOf } from '../../../prop-types';

import ConfigStore from '../../../store/ConfigStore';
import ProductStore from '../../../store/ProductStore';
import RequestState from '../../../types/RequestState';
import { paramsToQueryIdentifier } from '../../../util/query';
import ContentForState from '../../loader/ContentForState';

const MissingProductLoader = ({
  configStore,
  productStore,
  ids,
  onLoadSuccess,
  children,
  placeholder,
  ...rest
}) => {
  const missingIds = ids.filter(
    (productId) => !productStore.products.has(productId)
  );
  // Fetch when the selection is non-empty and anything is uncached. Use the
  // full `ids` list as the query identity so newly cached members or omitted
  // API products cannot shrink the key mid-flight into a different request.
  const needsFetch = ids.length > 0 && missingIds.length > 0;

  const getQueryParams = () => ({
    ids,
    // ID queries are paginated too; include every selected product on this page.
    resultsPerPage: ids.length,
    includeFilters: 0,
    excludeContent: 1,
    optimizeResponse: 1,
  });

  const getQueryIdentifier = () => paramsToQueryIdentifier(getQueryParams());

  if (needsFetch) {
    productStore.loadProducts(getQueryParams(), true).catch(() => {
      /* Store settles ERROR; ContentForState renders the error output. */
    });
  }

  const getCurrentSearchProducts = () =>
    // Skip products that were not found / omitted by the API.
    ids
      .map((productId) => productStore.products.get(productId))
      .filter((product) => !!product);

  const getCurrentSearchState = () => {
    if (!needsFetch) {
      return RequestState.LOADED;
    }
    return productStore.productQueryStates.get(getQueryIdentifier());
  };

  const state = getCurrentSearchState();
  const selectionKey = paramsToQueryIdentifier(ids);
  const notifiedSelectionRef = useRef(null);

  // Client-only loaded callback (analytics). Does not run during SSR.
  useEffect(() => {
    if (!onLoadSuccess || state !== RequestState.LOADED) {
      return;
    }
    if (notifiedSelectionRef.current === selectionKey) {
      return;
    }
    notifiedSelectionRef.current = selectionKey;
    onLoadSuccess(getCurrentSearchProducts());
  }, [selectionKey, state, onLoadSuccess]);

  return (
    <ContentForState
      state={state}
      error={productStore.lastError}
      forPlaceHolder={placeholder}
      forLoaded={() => children(getCurrentSearchProducts())}
      {...rest}
    />
  );
};

MissingProductLoader.propTypes = {
  configStore: modelOf(ConfigStore).isRequired,
  productStore: modelOf(ProductStore).isRequired,
  ids: PropTypes.array.isRequired,
  onLoadSuccess: PropTypes.func,
  placeholder: PropTypes.object,
};

export default inject(
  'productStore',
  'configStore'
)(observer(MissingProductLoader));
